import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API, { formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/app/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { User, Phone, MapTrifold, EnvelopeSimple, FloppyDisk, ArrowLeft, Bell } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import LegalitoConnectionCard from "@/components/profile/LegalitoConnectionCard";

export default function ProfileSettingsPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const isSolicitor = user?.role === "solicitor";
  const isClient = user?.role === "client";
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [officeAddress, setOfficeAddress] = useState(user?.office_address || "");
  const [saving, setSaving] = useState(false);

  // Client email-notification preferences
  const [emailPrefs, setEmailPrefs] = useState({ enabled: true, stage: true, message: true, mode: "instant" });
  const [emailPrefSaving, setEmailPrefSaving] = useState(false);

  // Notification preferences (solicitor only)
  const [prefs, setPrefs] = useState({ digest_unread_enabled: null, digest_unread_threshold_hours: null });
  const [globalPrefs, setGlobalPrefs] = useState({ unread_reminders_enabled: false, unread_threshold_hours: 24 });
  const [prefMode, setPrefMode] = useState("default"); // "default" | "on" | "off"
  const [prefThreshold, setPrefThreshold] = useState("");
  const [prefsSaving, setPrefsSaving] = useState(false);

  useEffect(() => {
    if (!isSolicitor) return;
    Promise.all([
      API.get("/auth/me/notification-prefs").then(r => r.data).catch(() => ({ digest_unread_enabled: null, digest_unread_threshold_hours: null })),
      API.get("/email-settings").then(r => r.data).catch(() => ({})),
    ]).then(([p, gs]) => {
      setPrefs(p);
      setGlobalPrefs({
        unread_reminders_enabled: !!gs.unread_reminders_enabled,
        unread_threshold_hours: gs.unread_threshold_hours || 24,
      });
      if (p.digest_unread_enabled === true) setPrefMode("on");
      else if (p.digest_unread_enabled === false) setPrefMode("off");
      else setPrefMode("default");
      setPrefThreshold(p.digest_unread_threshold_hours ? String(p.digest_unread_threshold_hours) : "");
    });
  }, [isSolicitor]);

  useEffect(() => {
    if (!isClient) return;
    API.get("/auth/me/email-notifications")
      .then((r) => setEmailPrefs({
        enabled: r.data?.enabled !== false,
        stage: r.data?.stage !== false,
        message: r.data?.message !== false,
        mode: r.data?.mode === "weekly" ? "weekly" : "instant",
      }))
      .catch(() => {});
  }, [isClient]);

  const saveEmailPref = async (patch) => {
    const prev = emailPrefs;
    setEmailPrefs({ ...emailPrefs, ...patch });
    setEmailPrefSaving(true);
    try {
      await API.put("/auth/me/email-notifications", patch);
      toast.success("Notification preferences updated");
    } catch (err) {
      setEmailPrefs(prev);
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setEmailPrefSaving(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.put("/auth/profile", {
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        office_address: officeAddress.trim() || undefined,
      });
      await checkAuth();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = name !== (user?.name || "") || phone !== (user?.phone || "") || officeAddress !== (user?.office_address || "");

  const handleSavePrefs = async () => {
    setPrefsSaving(true);
    try {
      if (prefMode === "default") {
        await API.put("/auth/me/notification-prefs", { clear_overrides: true });
        setPrefs({ digest_unread_enabled: null, digest_unread_threshold_hours: null });
        setPrefThreshold("");
      } else {
        const payload = { digest_unread_enabled: prefMode === "on" };
        if (prefMode === "on" && prefThreshold) {
          const h = Number(prefThreshold);
          if (!h || h < 1 || h > 168) {
            toast.error("Threshold must be between 1 and 168 hours");
            setPrefsSaving(false);
            return;
          }
          payload.digest_unread_threshold_hours = h;
        }
        const { data } = await API.put("/auth/me/notification-prefs", payload);
        setPrefs(data);
      }
      toast.success("Notification preferences saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPrefsSaving(false);
    }
  };

  const prefsHaveChanges = (() => {
    const currentMode = prefs.digest_unread_enabled === true ? "on" : prefs.digest_unread_enabled === false ? "off" : "default";
    if (prefMode !== currentMode) return true;
    if (prefMode === "on") {
      const currentT = prefs.digest_unread_threshold_hours ? String(prefs.digest_unread_threshold_hours) : "";
      if (prefThreshold !== currentT) return true;
    }
    return false;
  })();

  return (
    <div className="min-h-screen bg-slate-50" data-testid="profile-settings-page">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate(-1)} data-testid="settings-back-btn"
            className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors">
            <ArrowLeft className="w-4 h-4 text-slate-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Profile Settings
            </h1>
            <p className="text-sm text-slate-500 mt-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Manage your contact information visible to clients and colleagues
            </p>
          </div>
        </div>

        {/* Profile Card */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          {/* Avatar Header */}
          <div className="bg-slate-900 px-6 py-8 flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur flex items-center justify-center border-2 border-white/20">
              <span className="text-white text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {name?.charAt(0)?.toUpperCase() || "U"}
              </span>
            </div>
            <div>
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>{user?.name}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-200 uppercase tracking-wide">
                  {user?.role}
                </span>
                <span className="text-xs text-slate-400">{user?.email}</span>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSave} className="p-6 space-y-6" data-testid="profile-form">
            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <EnvelopeSimple weight="bold" className="w-4 h-4 text-slate-400" /> Email
              </Label>
              <Input
                value={user?.email || ""}
                disabled
                className="h-11 bg-slate-50 border-slate-200 text-slate-500 cursor-not-allowed"
                data-testid="profile-email"
              />
              <p className="text-[11px] text-slate-400">Email cannot be changed</p>
            </div>

            {/* Name */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <User weight="bold" className="w-4 h-4 text-slate-400" /> Full Name
              </Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="h-11 bg-white border-slate-200 focus:border-blue-600 focus:ring-blue-600/20"
                data-testid="profile-name-input"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <Phone weight="bold" className="w-4 h-4 text-slate-400" /> Phone Number
              </Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+44 7700 900123"
                className="h-11 bg-white border-slate-200 focus:border-blue-600 focus:ring-blue-600/20"
                data-testid="profile-phone-input"
              />
              <p className="text-[11px] text-slate-400">Visible to clients on their case detail view</p>
            </div>

            {/* Office Address */}
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
                <MapTrifold weight="bold" className="w-4 h-4 text-slate-400" /> Office Address
              </Label>
              <Input
                value={officeAddress}
                onChange={(e) => setOfficeAddress(e.target.value)}
                placeholder="10 High Street, London EC1A 1BB"
                className="h-11 bg-white border-slate-200 focus:border-blue-600 focus:ring-blue-600/20"
                data-testid="profile-office-input"
              />
              <p className="text-[11px] text-slate-400">Shown on the client portal and case detail pages</p>
            </div>

            {/* Save */}
            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <p className={`text-xs font-medium transition-opacity ${hasChanges ? "text-amber-600 opacity-100" : "opacity-0"}`}>
                Unsaved changes
              </p>
              <Button
                type="submit"
                disabled={saving || !hasChanges}
                data-testid="save-profile-btn"
                className="bg-slate-900 hover:bg-slate-800 text-white h-10 px-6"
              >
                <FloppyDisk weight="bold" className="w-4 h-4 mr-2" />
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>

        {/* Email notifications (Client only) */}
        {isClient && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mt-6" data-testid="client-email-notif-card">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <Bell weight="fill" className="w-4 h-4 text-slate-700" />
              <div>
                <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Email Notifications</h3>
                <p className="text-xs text-slate-500">Choose how and when we email you about your case.</p>
              </div>
            </div>
            <div className="p-6 space-y-5">
              {/* Master switch */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="pr-4">
                  <p className="text-sm font-medium text-slate-900">Case update emails</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">Master switch. You'll always see updates in your portal either way.</p>
                </div>
                <Switch
                  checked={emailPrefs.enabled}
                  disabled={emailPrefSaving}
                  onCheckedChange={(v) => saveEmailPref({ enabled: v })}
                  data-testid="client-email-notif-switch"
                />
              </div>

              {emailPrefs.enabled && (
                <>
                  {/* Delivery frequency */}
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">How often</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { v: "instant", label: "As it happens", hint: "Email me for each update" },
                        { v: "weekly", label: "Weekly summary", hint: "One email every Monday" },
                      ].map((opt) => (
                        <button key={opt.v} type="button"
                          data-testid={`email-mode-${opt.v}`}
                          disabled={emailPrefSaving}
                          onClick={() => saveEmailPref({ mode: opt.v })}
                          className={`flex flex-col items-start text-left p-3 rounded-md border transition-colors ${emailPrefs.mode === opt.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                          <span className="text-xs font-semibold">{opt.label}</span>
                          <span className={`text-[10px] mt-0.5 ${emailPrefs.mode === opt.v ? "text-slate-300" : "text-slate-500"}`}>{opt.hint}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Per-type toggles */}
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">What to email me about</Label>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-3 bg-white rounded-md border border-slate-200">
                        <div className="pr-4">
                          <p className="text-sm font-medium text-slate-900">Stage updates</p>
                          <p className="text-[11px] text-slate-500 mt-0.5">When a stage of your transaction completes.</p>
                        </div>
                        <Switch
                          checked={emailPrefs.stage}
                          disabled={emailPrefSaving}
                          onCheckedChange={(v) => saveEmailPref({ stage: v })}
                          data-testid="email-pref-stage-switch"
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 bg-white rounded-md border border-slate-200">
                        <div className="pr-4">
                          <p className="text-sm font-medium text-slate-900">New messages</p>
                          <p className="text-[11px] text-slate-500 mt-0.5">When your solicitor sends you a message.</p>
                        </div>
                        <Switch
                          checked={emailPrefs.message}
                          disabled={emailPrefSaving}
                          onCheckedChange={(v) => saveEmailPref({ message: v })}
                          data-testid="email-pref-message-switch"
                        />
                      </div>
                    </div>
                    {emailPrefs.mode === "weekly" && (
                      <p className="text-[11px] text-slate-400 mt-2">Your weekly summary includes only the update types switched on above.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Notification Preferences (Solicitor only) */}
        {isSolicitor && (
          <div className="mt-6">
            <LegalitoConnectionCard />
          </div>
        )}

        {/* Notification Preferences (Solicitor only) */}
        {isSolicitor && (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden mt-6" data-testid="notification-prefs-card">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <Bell weight="fill" className="w-4 h-4 text-slate-700" />
              <div>
                <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Notification Preferences</h3>
                <p className="text-xs text-slate-500">Personal overrides for the unread-message reminder digest.</p>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className={`text-xs rounded-md p-3 border ${globalPrefs.unread_reminders_enabled ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                <span className="font-semibold">Firm-wide setting:</span> Reminders are{" "}
                <strong>{globalPrefs.unread_reminders_enabled ? "ON" : "OFF"}</strong>{" "}
                with a default threshold of <strong>{globalPrefs.unread_threshold_hours}h</strong>.
                {!globalPrefs.unread_reminders_enabled && " (Your personal toggle won't fire while this is OFF.)"}
              </div>

              {/* Mode selector */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Digest delivery for me</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { v: "default", label: "Use default", hint: "Follow firm-wide setting" },
                    { v: "on", label: "Always on", hint: "Send me reminders" },
                    { v: "off", label: "Always off", hint: "Skip me entirely" },
                  ].map((opt) => (
                    <button key={opt.v} type="button"
                      data-testid={`pref-mode-${opt.v}`}
                      onClick={() => setPrefMode(opt.v)}
                      className={`flex flex-col items-start text-left p-3 rounded-md border transition-colors ${prefMode === opt.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
                      <span className="text-xs font-semibold">{opt.label}</span>
                      <span className={`text-[10px] mt-0.5 ${prefMode === opt.v ? "text-slate-300" : "text-slate-500"}`}>{opt.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Threshold (only when "on") */}
              {prefMode === "on" && (
                <div>
                  <Label htmlFor="my-threshold" className="text-sm font-medium text-slate-700">My threshold (hours)</Label>
                  <Input id="my-threshold" data-testid="pref-threshold-input" type="number" min="1" max="168"
                    value={prefThreshold}
                    onChange={(e) => setPrefThreshold(e.target.value)}
                    placeholder={`Leave blank to use default (${globalPrefs.unread_threshold_hours}h)`}
                    className="mt-1.5 max-w-[240px]" />
                  <p className="text-[11px] text-slate-400 mt-1">Send me reminders for messages unread this long. 1–168 hours. Blank = use firm default.</p>
                </div>
              )}

              <div className="flex justify-end pt-2 border-t border-slate-100">
                <Button onClick={handleSavePrefs} disabled={prefsSaving || !prefsHaveChanges}
                  data-testid="save-prefs-btn"
                  className="bg-slate-900 hover:bg-slate-800 text-white">
                  <FloppyDisk weight="bold" className="w-4 h-4 mr-2" />
                  {prefsSaving ? "Saving..." : "Save Preferences"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
